import { lazy, Suspense } from "react";
import { Redirect } from "react-router-dom";
import ContentHeader from "../../components/app/headers/ContentHeader";
import Loader from "../../components/Loader";
import { useApiQuery } from "../../services/api";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";

const LazyPdfViewer = lazy(() => import("../../components/PdfViewer"));

/**
 * In-app viewer for a congress-space PDF card. PDFs must NOT be opened with
 * `window.open`: the native iOS/Android shells load the raw PDF URL in the
 * main webview, whose built-in PDF renderer draws edge-to-edge under the
 * status bar (no safe-area) and leaves no way back to the app.
 */
export default function CongressPdfPage({
  roomId,
  spaceId,
  cardId,
}: {
  roomId: string;
  spaceId: string;
  cardId: string;
}) {
  const { contentLang } = useActiveLang();
  const result = useApiQuery("/webapp/private/congress-spaces/:id/content", {
    param: { id: spaceId },
    query: { language: contentLang },
  });

  if (result.state === "pending") return <Loader expanded />;
  if (result.state === "error") {
    return <Redirect to={`/room/resources/${roomId}`} />;
  }

  const block = result.body.find((entry) => entry.id === cardId);
  if (
    block === undefined ||
    block.kind !== "pdfCard" ||
    // An IMAGE document has no PDF and never routes here — it opens in a
    // lightbox on the Resources tab. A deep link to this route for one is as
    // stale as a link to a deleted card, so it redirects the same way.
    // `pdfUrl` is null exactly when documentKind is "image" (the DB CHECK
    // guarantees it), and testing the url is what narrows the type for the
    // viewer below.
    block.pdfUrl === null
  ) {
    // Stale deep link: the card was deleted, reordered away, or is filed under
    // a language other than the reader's — congress content has no fallback.
    return <Redirect to={`/room/resources/${roomId}`} />;
  }

  return (
    <div className="fixed top-0 left-0 box-border flex h-dvh w-full flex-col bg-white pt-(--safe-area-top)">
      <ContentHeader className="md:block" title={block.documentTitle} />
      <div
        className="min-h-0 w-full flex-1 overflow-auto md:mx-auto md:max-w-xl"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <Suspense fallback={<Loader expanded />}>
          <LazyPdfViewer
            url={block.pdfUrl}
            loadingFallback={<Loader expanded />}
            controls={{ kind: "floating" }}
            display={{ kind: "continuous" }}
          />
        </Suspense>
      </div>
    </div>
  );
}
